import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Search, Loader2, CheckCircle2, MapPin, Calendar, AlertTriangle, ArrowRight } from "lucide-react";
import { trackWardIssue, formatApiError } from "@/lib/api";
import ResolvedCardShare from "@/components/public/ResolvedCardShare";

const STEPS = ["New", "Reviewed", "In Progress", "Resolved"];

function statusIndex(status) {
  if (status === "Escalated") return 2;
  const idx = STEPS.indexOf(status);
  return idx === -1 ? 0 : idx;
}

export default function TrackWidget({ initialRef = "" }) {
  const [ref, setRef] = useState(initialRef);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const search = async (value) => {
    const cleaned = (value || "").trim();
    if (!cleaned) {
      setError("Please enter your reference ID (e.g. HW29-A1B2C3).");
      return;
    }
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const data = await trackWardIssue(cleaned);
      setResult(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not find that issue.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialRef) search(initialRef);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRef]);

  const currentIdx = result ? statusIndex(result.status) : 0;

  return (
    <div className="glass-dark rounded-3xl p-6 md:p-10" data-testid="track-widget">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          search(ref);
        }}
        className="flex flex-col sm:flex-row gap-3"
      >
        <input
          value={ref}
          onChange={(e) => setRef(e.target.value.toUpperCase())}
          placeholder="HW29-XXXXXX"
          data-testid="track-input"
          className="flex-1 px-5 py-4 rounded-xl bg-navy/60 border border-white/15 text-white font-mono tracking-widest placeholder:text-slate-500 focus:border-gold focus:ring-2 focus:ring-gold/30 outline-none"
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="track-submit"
          className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl bg-gold text-white font-bold btn-saffron disabled:opacity-60"
        >
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}
          Track Issue
        </button>
      </form>

      {error && (
        <p className="mt-5 flex items-center gap-2 text-sm text-red-300" data-testid="track-error">
          <AlertTriangle className="h-4 w-4 shrink-0" /> {error}
        </p>
      )}

      {result && (
        <div className="mt-8" data-testid="track-result">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold">{result.referenceId}</p>
              <h3 className="font-display text-xl md:text-2xl font-bold text-white mt-1">{result.title}</h3>
            </div>
            <span
              data-testid="track-status"
              className={`px-4 py-1.5 rounded-full font-mono text-xs font-bold uppercase tracking-wider ${
                result.status === "Resolved"
                  ? "bg-civic text-white"
                  : result.status === "Escalated"
                  ? "bg-red-500 text-white"
                  : "bg-gold text-white"
              }`}
            >
              {result.status}
            </span>
          </div>

          {/* Progress stepper */}
          <div className="flex items-center" data-testid="track-stepper">
            {STEPS.map((s, i) => (
              <React.Fragment key={s}>
                <div className="flex flex-col items-center shrink-0">
                  <div
                    className={`h-8 w-8 rounded-full flex items-center justify-center border-2 ${
                      i <= currentIdx ? "bg-gold border-gold text-white" : "border-white/20 text-slate-500"
                    }`}
                  >
                    {i < currentIdx || (i === currentIdx && result.status === "Resolved") ? (
                      <CheckCircle2 className="h-4 w-4" />
                    ) : (
                      <span className="font-mono text-xs">{i + 1}</span>
                    )}
                  </div>
                  <p className={`mt-2 font-mono text-[9px] md:text-[10px] uppercase tracking-wider ${i <= currentIdx ? "text-white" : "text-slate-500"}`}>
                    {s}
                  </p>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-1 md:mx-2 -mt-5 ${i < currentIdx ? "bg-gold" : "bg-white/15"}`} />
                )}
              </React.Fragment>
            ))}
          </div>

          <div className="grid sm:grid-cols-3 gap-3 mt-7 text-sm">
            <div className="rounded-xl border border-white/10 px-4 py-3">
              <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-1">Category</p>
              <p className="text-white font-semibold">{result.category}</p>
            </div>
            <div className="rounded-xl border border-white/10 px-4 py-3">
              <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-1 flex items-center gap-1"><MapPin className="h-3 w-3" /> Locality</p>
              <p className="text-white font-semibold">{result.locality}</p>
            </div>
            <div className="rounded-xl border border-white/10 px-4 py-3">
              <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-1 flex items-center gap-1"><Calendar className="h-3 w-3" /> Submitted</p>
              <p className="text-white font-semibold">{(result.created_at || "").slice(0, 10)}</p>
            </div>
          </div>

          {result.status === "Resolved" && (
            <ResolvedCardShare referenceId={result.referenceId} title={result.title} />
          )}
        </div>
      )}

      {!result && !error && (
        <p className="mt-5 text-sm text-slate-400">
          Submitted an issue? Use the reference ID you received (e.g. HW29-A1B2C3).{" "}
          <Link to="/submit-ward-issue" className="text-gold font-semibold inline-flex items-center gap-1 hover:underline">
            Report a new issue <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </p>
      )}
    </div>
  );
}
